import { useCallback, useEffect } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useBeforeUnload, useBlocker, useConfirmDialog } from '@shared/hooks';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import { useDesignWorkspaceDirty } from '@shared/designVariants/useDesignWorkspaceDirty';

/** One discard decision covers native browser navigation and workspace/appearance controls. */
export const useWorkspaceDraftGuard = (dirty: boolean, discard: () => void) => {
  const { module, registerGuard } = useDesignWorkspace();
  useDesignWorkspaceDirty(dirty);
  const { confirm, dialogProps } = useConfirmDialog(dirty);
  const approve = useCallback(async () => {
    if (!dirty) return true;
    if (!(await confirm())) return false;
    discard();
    return true;
  }, [dirty, confirm, discard]);
  const request = useCallback(
    (action: () => void) => {
      if (!dirty) action();
      else
        void approve().then((allowed) => {
          if (allowed) action();
        });
    },
    [dirty, approve],
  );
  useBeforeUnload(dirty);
  useBlocker(dirty, approve);
  useEffect(() => {
    if (module) return;
    registerGuard(request);
    return () => registerGuard(null);
  }, [module, registerGuard, request]);
  const dialog = (
    <Dialog
      open={dialogProps.showDialogFlag}
      onClose={dialogProps.handleClose}
      aria-labelledby="workspace-editor-discard-title">
      <DialogTitle id="workspace-editor-discard-title">Discard unsaved changes?</DialogTitle>
      <DialogContent>
        Your saved data stays unchanged. Discard the current editor draft before leaving.
      </DialogContent>
      <DialogActions>
        <Button onClick={dialogProps.handleClose}>Keep editing</Button>
        <Button color="error" onClick={dialogProps.confirmDialog}>
          Discard changes
        </Button>
      </DialogActions>
    </Dialog>
  );
  return { request, dialog };
};
